import React from 'react';
import { Link, graphql, useStaticQuery } from 'gatsby';
import { Tab } from '@headlessui/react';

import Layout from '../../components/layout';
import SEO from '../../components/seo';
import ScrollToTop from '../../components/scroll-to-top';

import { classNames } from '../../utils';
import { durationToSeconds } from '../../utils/duration-to-seconds';

const tabPanelClasses = classNames('pt-3', 'focus:outline-none');
const tabClasses = ({ selected }) =>
  classNames(
    'w-full py-2.5 leading-relaxed',
    'focus:outline-none',
    selected ? 'bg-gray-800 shadow-2xl' : ''
  );

const AllBooks = ({ allData }) => (
  <ul className="flex flex-col gap-y-12 mt-10 list-none">
    {allData.map((edge, idx) => {
      const {
        id,
        guest,
        tagline,
        videoId,
        publishedAt,
        booksMentioned,
        fields: { slug },
      } = edge.node;

      return (
        <li key={idx} className="border-b border-gray-800 pb-10">
          <div id={`${id}`}>
            <span className="text-sm block">{publishedAt}</span>
            <Link to={`/episodes/${slug}`} className="text-lg md:text-2xl mt-1">
              #{id} - {guest}
            </Link>
            <div className="mt-5 mb-2 flex flex-row flex-wrap">
              {booksMentioned.map((book, idx) => {
                const {
                  title,
                  author,
                  published,
                  amazon,
                  imageUrl,
                  wikipedia,
                  goodreads,
                  timestamp,
                } = book;

                return (
                  <div
                    className="flex gap-3 pr-5 py-2 md:w-1/2"
                    key={`book-${id}-${idx}`}
                  >
                    <a
                      href={amazon}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="max-w-24 md:w-1/6"
                    >
                      <img
                        key={idx}
                        src={imageUrl}
                        alt={title}
                        width="90"
                        loading="lazy"
                        className="flex-shrink-0 rounded-sm mt-1"
                      />
                    </a>
                    <div className="flex flex-col gap-3 w-5/6">
                      <span>
                        <a
                          href={amazon}
                          className="text-md"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {title}
                        </a>
                      </span>
                      <span className="-mt-3 text-sm">{author}</span>
                      {published && (
                        <span className="-mt-3 text-sm">
                          Published:{' '}
                          <span className="text-gray-400">{published}</span>{' '}
                        </span>
                      )}
                      {timestamp && (
                        <span className="-mt-3 text-sm">
                          Timestamp:{' '}
                          <a
                            href={`https://www.youtube.com/watch?v=${videoId}&t=${durationToSeconds(
                              timestamp
                            )}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {timestamp}
                          </a>{' '}
                        </span>
                      )}
                      <div className="flex gap-2 -mt-3">
                        {wikipedia && (
                          <a
                            href={wikipedia}
                            className="text-sm border-b border-gray-700"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Wikipedia
                          </a>
                        )}
                        {goodreads && (
                          <a
                            href={goodreads}
                            className="text-sm border-b border-gray-700"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Goodreads
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </li>
      );
    })}
  </ul>
);

const FrequentlyRecBooks = ({ freqData }) => (
  <div className="flex justify-between flex-wrap gap-y-10 gap-x-5 mt-10">
    {freqData.map((book, idx) => {
      const {
        title,
        author,
        published,
        amazon,
        imageUrl,
        wikipedia,
        goodreads,
        mentionedIn,
      } = book;
      return (
        <div key={idx} className="flex flex-col">
          <a href={amazon} target="_blank" rel="noopener noreferrer">
            <img
              key={idx}
              src={imageUrl}
              alt={title}
              width="200"
              className="rounded-sm mb-5"
              loading="lazy"
            />
          </a>
          <div className="flex flex-col w-52 max-w-xs gap-4">
            <a
              href={amazon}
              className="text-gradient text-md"
              target="_blank"
              rel="noopener noreferrer"
            >
              {title}
            </a>
            <span className="-mt-3 text-sm">{author}</span>
            {published && (
              <span className="-mt-3 text-sm">
                Published: <span className="text-gray-400">{published}</span>{' '}
              </span>
            )}
            <div className="flex gap-2 -mt-3">
              {wikipedia && (
                <a
                  href={wikipedia}
                  className="text-sm border-b border-gray-700"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Wikipedia
                </a>
              )}
              {goodreads && (
                <a
                  href={goodreads}
                  className="text-sm border-b border-gray-700"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Goodreads
                </a>
              )}
            </div>
            <span className="-mt-3 text-sm">
              Mentioned in:{' '}
              {mentionedIn.map((ep) => (
                <Link
                  to={`/episodes/${ep.slug}`}
                  key={ep.id}
                  className="mr-1 border-b border-gray-700"
                >
                  {ep.id}
                </Link>
              ))}
            </span>
          </div>
        </div>
      );
    })}
  </div>
);

const BooksPage = () => {
  const data = useStaticQuery(graphql`
    query {
      allEpisodesJson(
        filter: { booksMentioned: { elemMatch: { title: { ne: null } } } }
      ) {
        edges {
          node {
            id
            guest
            tagline
            videoId
            publishedAt(formatString: "ddd, MMM D, YYYY")
            booksMentioned {
              title
              author
              published(formatString: "MMM D, YYYY")
              wikipedia
              goodreads
              amazon
              imageUrl
              timestamp
            }
            fields {
              slug
            }
          }
        }
        group(field: booksMentioned___title) {
          totalCount
          fieldValue
          edges {
            node {
              id
              guest
              booksMentioned {
                title
                author
                published(formatString: "MMM D, YYYY")
                wikipedia
                goodreads
                amazon
                imageUrl
              }
              fields {
                slug
              }
            }
          }
        }
      }
    }
  `);

  const topBooks = [...data.allEpisodesJson.group]
    .sort((a, b) => b.totalCount - a.totalCount)
    .slice(0, 16);

  const topBooksDetails = topBooks.map((topBook) =>
    topBook.edges[0].node.booksMentioned.find(
      (book) => book.title === topBook.fieldValue
    )
  );

  topBooksDetails.forEach((book, idx) => {
    book.mentionedIn = topBooks[idx].edges.map((edge) => ({
      id: edge.node.id,
      slug: edge.node.fields.slug,
    }));
  });

  return (
    <Layout>
      <SEO title="Books mentioned" />
      <div>
        <h2>Books mentioned</h2>
        <div className="px-2 mt-10 sm:px-0">
          <Tab.Group>
            <Tab.List className="flex p-1 space-x-1 rounded-md ring-1 ring-inset ring-gray-700">
              <Tab className={tabClasses}>All books</Tab>
              <Tab className={tabClasses}>Frequently mentioned</Tab>
            </Tab.List>
            <Tab.Panels className="mt-2">
              <Tab.Panel className={tabPanelClasses}>
                <AllBooks allData={data.allEpisodesJson.edges} />
              </Tab.Panel>
              <Tab.Panel className={tabPanelClasses}>
                <FrequentlyRecBooks freqData={topBooksDetails} />
              </Tab.Panel>
            </Tab.Panels>
          </Tab.Group>
        </div>
        <ScrollToTop />
      </div>
    </Layout>
  );
};

export default BooksPage;
